/**
 * Push Notification Application Service
 * Orchestrates push notification operations
 */

import { getFirebaseMessagingAdapter, type FirebaseMessagingAdapter } from '../../infra/adapters/firebase.adapter';
import type { PushNotificationPayload, PushSubscriptionData } from '../../domain/models/PushNotification';
import { getItem, setItem } from '@/core/services/storage/utils/localStorageUtil';

const DEVICE_TOKEN_KEY = 'deviceToken';
const ASK_AGAIN_KEY = 'pushNotificationAskAgain';
const ASK_AGAIN_DAYS = 7;

/**
 * Push Notification Service
 * Application layer service for managing push notifications
 */
export class PushNotificationService {
  private adapter: FirebaseMessagingAdapter;
  private messageUnsubscribe: (() => void) | null = null;

  constructor(adapter?: FirebaseMessagingAdapter) {
    this.adapter = adapter || getFirebaseMessagingAdapter();
  }

  /**
   * Initialize the push notification service
   */
  async initialize(): Promise<void> {
    await this.adapter.initialize();

    // If permission already granted, refresh token
    if (this.adapter.getPermissionStatus() === 'granted') {
      await this.refreshToken();
    }
  }

  /**
   * Check if push notifications are supported
   */
  isSupported(): boolean {
    return this.adapter.isSupported();
  }

  /**
   * Get the current permission status
   */
  getPermissionStatus(): NotificationPermission {
    return this.adapter.getPermissionStatus();
  }

  /**
   * Check if we should show the permission prompt
   * Returns false if:
   * - Already granted
   * - Already denied
   * - User dismissed recently (within ASK_AGAIN_DAYS)
   */
  shouldShowPrompt(): boolean {
    if (!this.adapter.isSupported()) {
      return false;
    }

    const permission = this.adapter.getPermissionStatus();
    
    // Don't show if already granted or denied
    if (permission !== 'default') {
      return false;
    }

    // Check if user dismissed recently
    const askAgain = getItem<{ timestamp: number }>(ASK_AGAIN_KEY);
    if (askAgain) {
      const daysPassed = (Date.now() - askAgain.timestamp) / (1000 * 60 * 60 * 24);
      if (daysPassed < ASK_AGAIN_DAYS) {
        return false;
      }
    }

    return true;
  }

  /**
   * Mark that user dismissed the prompt (don't ask again for a while)
   */
  dismissPrompt(): void {
    setItem(ASK_AGAIN_KEY, { timestamp: Date.now() });
  }

  /**
   * Request permission and subscribe to push notifications
   */
  async requestPermissionAndSubscribe(
    customerId?: string,
    visitorId?: string
  ): Promise<string | null> {
    const token = await this.adapter.requestPermission();

    if (token) {
      await this.saveAndSyncToken(token, customerId, visitorId);
    }

    return token;
  }

  /**
   * Refresh token and sync with backend if changed
   */
  private async refreshToken(customerId?: string, visitorId?: string): Promise<void> {
    const token = await this.adapter.getToken();
    
    if (token) {
      const storedToken = getItem<string>(DEVICE_TOKEN_KEY);
      
      if (token !== storedToken) {
        await this.saveAndSyncToken(token, customerId, visitorId);
      }
    }
  }

  /**
   * Save token locally and sync with backend
   */
  private async saveAndSyncToken(
    token: string,
    customerId?: string,
    visitorId?: string
  ): Promise<void> {
    try {
      const subscriptionData: PushSubscriptionData = {
        deviceToken: token,
        customerId,
        visitorId,
        client: 'WEBSITE',
      };

      await this.adapter.subscribeToBackend(subscriptionData);
      setItem(DEVICE_TOKEN_KEY, token);

    } catch (error) {
      console.error('Failed to sync token with backend:', error);
    }
  }

  /**
   * Subscribe to foreground messages
   */
  onMessage(callback: (payload: PushNotificationPayload) => void): void {
    // Unsubscribe from previous listener if any
    if (this.messageUnsubscribe) {
      this.messageUnsubscribe();
    }

    this.messageUnsubscribe = this.adapter.onMessage(callback);
  }

  /**
   * Show a browser notification (for foreground messages)
   */
  showNotification(payload: PushNotificationPayload): void {
    if (Notification.permission !== 'granted') {
      return;
    }

    // Note: 'image' is not part of standard NotificationOptions but is supported by some browsers
    const notificationOptions: NotificationOptions & { image?: string } = {
      body: payload.body,
      icon: payload.icon,
      image: payload.image,
    };

    const notification = new Notification(payload.title, notificationOptions);

    // Auto-close after 10 seconds
    setTimeout(() => {
      notification.close();
    }, 10000);

    // Handle click
    notification.addEventListener('click', () => {
      notification.close();
      if (payload.clickAction) {
        window.open(payload.clickAction, '_blank');
      }
    });
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.messageUnsubscribe) {
      this.messageUnsubscribe();
      this.messageUnsubscribe = null;
    }
  }
}

/**
 * Singleton instance
 */
let serviceInstance: PushNotificationService | null = null;

/**
 * Get or create push notification service instance
 */
export function getPushNotificationService(): PushNotificationService {
  if (!serviceInstance) {
    serviceInstance = new PushNotificationService();
  }
  return serviceInstance;
}
